import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Spinner, Table, Card, CardBody } from 'reactstrap';
import usePermission from '../Hooks/usePermission';
import {
  fetchContractorCost,
  createContractorCost,
  updateContractorCost,
  deleteContractorCost,
  fetchWorkOrderCost,
} from '../store/workOrderCost/actions';
import { formatCurrency } from '../helpers/formatCurrency';

const MISSING_LABEL_KEY = {
  PARTS_UNIT_PRICE_MISSING: 'cost_tracking.wo_total.reason_parts_price_missing',
  LABOR_RATE_MISSING_FOR_TECH: 'cost_tracking.wo_total.reason_labor_rate_missing',
  DOWNTIME_RATE_MISSING: 'cost_tracking.wo_total.reason_downtime_rate_missing',
};

/**
 * Cost tab content: contractor cost line items + WO total + missing-data banner.
 * Status gates and ownership are enforced server-side; UI mirrors them to hide actions.
 */
const WoCostSection = ({ workOrderId, workOrderStatus, currentUserId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const lines = useSelector((s) => s.WorkOrderCost?.lines || []);
  const linesLoading = useSelector((s) => s.WorkOrderCost?.linesLoading);
  // Fall back to the active org's currency when no cost record exists yet (new WO).
  const activeOrgCurrency = useSelector((s) => s.ActiveContext?.activeOrganizationCurrency);
  const linesError = useSelector((s) => s.WorkOrderCost?.linesError);
  const cost = useSelector((s) => s.WorkOrderCost?.cost);
  const costLoading = useSelector((s) => s.WorkOrderCost?.costLoading);
  const mutating = useSelector((s) => s.WorkOrderCost?.mutating);

  const canEnterContractor = hasPermission('cost:enter-contractor-cost');
  const canReadWoTotal = hasPermission('cost:read-wo-total');
  const isAdmin = hasPermission('time-entry:log-others');

  const statusBlocksMutation = workOrderStatus === 'SUBMITTED'
    || (workOrderStatus === 'RESOLVED' && !isAdmin);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ id: null, description: '', amount: '' });
  const [formErr, setFormErr] = useState(null);
  const [includeDowntime, setIncludeDowntime] = useState(true);

  const displayedTotalCost = useMemo(() => {
    if (!cost) return null;
    if (includeDowntime) return cost.totalCost;
    const total = parseFloat(cost.totalCost);
    const downtime = parseFloat(cost.downtimeCost);
    if (Number.isNaN(total)) return cost.totalCost;
    return total - (Number.isNaN(downtime) ? 0 : downtime);
  }, [cost, includeDowntime]);

  useEffect(() => {
    if (!workOrderId) return;
    if (canEnterContractor) dispatch(fetchContractorCost(workOrderId));
    if (canReadWoTotal) dispatch(fetchWorkOrderCost(workOrderId));
  }, [dispatch, workOrderId, canEnterContractor, canReadWoTotal]);

  const refresh = () => {
    if (canEnterContractor) dispatch(fetchContractorCost(workOrderId));
    if (canReadWoTotal) dispatch(fetchWorkOrderCost(workOrderId));
  };

  const openCreate = () => {
    setForm({ id: null, description: '', amount: '' });
    setFormErr(null);
    setModalOpen(true);
  };

  const openEdit = (line) => {
    setForm({ id: line.id, description: line.description, amount: line.amount });
    setFormErr(null);
    setModalOpen(true);
  };

  const handleSave = () => {
    setFormErr(null);
    const desc = (form.description || '').trim();
    const amount = form.amount === '' ? NaN : Number(form.amount);
    if (!desc) {
      setFormErr(t('cost_tracking.contractor.error_description_required', 'Description is required.'));
      return;
    }
    if (Number.isNaN(amount) || amount < 0) {
      setFormErr(t('cost_tracking.contractor.error_amount_invalid', 'Amount must be 0 or higher.'));
      return;
    }
    const onDone = (err) => {
      if (err) {
        setFormErr(err);
        return;
      }
      setModalOpen(false);
      refresh();
    };
    if (form.id) {
      dispatch(updateContractorCost(form.id, { description: desc, amount }, onDone));
    } else {
      dispatch(createContractorCost({ workOrderId, description: desc, amount }, onDone));
    }
  };

  const handleDelete = (line) => {
    dispatch(deleteContractorCost(line.id, (err) => {
      if (!err) refresh();
    }));
  };

  const canEditLine = (line) =>
    isAdmin || (currentUserId != null && line.createdBy === currentUserId);

  return (
    <div className="wo-cost-section">
      {/* WO Total summary — admin only — headline first */}
      {canReadWoTotal && (
        <Card className="mb-3 border-0 shadow-sm">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: '12px' }}>
              <h5 className="mb-0">{t('cost_tracking.wo_total.title', 'Work order total')}</h5>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="woIncludeDowntimeSwitch"
                  checked={includeDowntime}
                  onChange={(e) => setIncludeDowntime(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="woIncludeDowntimeSwitch">
                  {t('cost_tracking.asset_tco.include_downtime', 'Include downtime cost')}
                </label>
              </div>
            </div>
            {costLoading && (
              <div className="text-center py-3">
                <Spinner size="sm" />
              </div>
            )}
            {!costLoading && cost && (
              <>
                {cost.missingDataReasons?.length > 0 && (
                  <div className="alert alert-warning">
                    <div className="fw-semibold mb-1">
                      {t('cost_tracking.wo_total.missing_banner', '{{count}} cost input(s) missing — total may be understated',
                        { count: cost.missingDataReasons.length })}
                    </div>
                    <ul className="mb-0 ps-3 small">
                      {cost.missingDataReasons.map((reason) => (
                        <li key={reason}>{t(MISSING_LABEL_KEY[reason] || 'shared.n_a', reason)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <Table size="sm" borderless className="mb-0">
                  <tbody>
                    <tr>
                      <td>{t('cost_tracking.wo_total.labor', 'Labor')}</td>
                      <td className="text-end">{formatCurrency(cost.laborCost, cost.currency)}</td>
                    </tr>
                    <tr>
                      <td>{t('cost_tracking.wo_total.parts', 'Parts')}</td>
                      <td className="text-end">{formatCurrency(cost.partsCost, cost.currency)}</td>
                    </tr>
                    <tr>
                      <td>{t('cost_tracking.wo_total.contractor', 'Contractor')}</td>
                      <td className="text-end">{formatCurrency(cost.contractorCost, cost.currency)}</td>
                    </tr>
                    <tr className={includeDowntime ? '' : 'text-muted'} style={!includeDowntime ? { textDecoration: 'line-through' } : undefined}>
                      <td>{t('cost_tracking.wo_total.downtime', 'Downtime')}</td>
                      <td className="text-end">{formatCurrency(cost.downtimeCost, cost.currency)}</td>
                    </tr>
                    <tr className="fw-bold border-top">
                      <td>{t('cost_tracking.wo_total.total', 'Total')}</td>
                      <td className="text-end">{formatCurrency(displayedTotalCost, cost.currency)}</td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {/* Contractor cost subsection — supporting detail */}
      {canEnterContractor && (
        <Card className="mb-3 border-0 shadow-sm">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 className="mb-1">{t('cost_tracking.contractor.section_title', 'Contractor cost')}</h5>
                <small className="text-muted">
                  {t('cost_tracking.contractor.section_subtitle', 'External / contractor invoices for this work order')}
                </small>
              </div>
              <Button
                color="primary"
                size="sm"
                disabled={mutating || statusBlocksMutation}
                onClick={openCreate}
              >
                {t('cost_tracking.contractor.add', 'Add line')}
              </Button>
            </div>

            {linesLoading && (
              <div className="text-center py-3">
                <Spinner size="sm" /> {t('cost_tracking.contractor.loading', 'Loading…')}
              </div>
            )}
            {linesError && <div className="alert alert-danger">{linesError}</div>}
            {!linesLoading && lines.length === 0 && (
              <div className="text-muted text-center py-3">
                {t('cost_tracking.contractor.no_lines', 'No contractor cost on this work order yet.')}
              </div>
            )}
            {lines.length > 0 && (
              <Table responsive size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>{t('cost_tracking.contractor.table.description', 'Description')}</th>
                    <th className="text-end">{t('cost_tracking.contractor.table.amount', 'Amount')}</th>
                    <th style={{ width: '120px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.id}>
                      <td>{line.description}</td>
                      <td className="text-end">{formatCurrency(line.amount, cost?.currency)}</td>
                      <td className="text-end">
                        {canEditLine(line) && !statusBlocksMutation && (
                          <>
                            <Button size="sm" color="link" className="p-0 me-2" onClick={() => openEdit(line)}>
                              {t('shared.edit', 'Edit')}
                            </Button>
                            <Button size="sm" color="link" className="text-danger p-0" onClick={() => handleDelete(line)}>
                              {t('shared.delete', 'Delete')}
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      )}

      {/* Add / edit modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">
            {form.id
              ? t('cost_tracking.contractor.edit_title', 'Edit contractor cost')
              : t('cost_tracking.contractor.add_title', 'Add contractor cost')}
          </h5>
          <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
        </div>
        <div className="modal-body">
          {formErr && <div className="alert alert-danger">{formErr}</div>}
          <div className="mb-3">
            <label className="form-label">
              {t('cost_tracking.contractor.field_description', 'Description')}
            </label>
            <input
              type="text"
              className="form-control"
              maxLength={500}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t('cost_tracking.contractor.field_amount', 'Amount')}
            </label>
            <div className="input-group">
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
              <span className="input-group-text">{cost?.currency || activeOrgCurrency || 'USD'}</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button color="secondary" outline disabled={mutating} onClick={() => setModalOpen(false)}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="primary" disabled={mutating} onClick={handleSave}>
            {t('shared.save', 'Save')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default WoCostSection;
